import { routes } from "pages/routes";

type RouteLocation = {
  pathname?: string | null;
  search?: string | null;
};

export function getExerciseListFilter(location: RouteLocation) {
  if (!routes.exercises.match(location.pathname)) {
    return null;
  }

  const searchParams = new URLSearchParams(location.search ?? "");

  return {
    levelId: searchParams.get("level_id"),
    tagId: searchParams.get("tag_id"),
    sourceId: searchParams.get("source_id"),
  };
}

export type ActiveExerciseFilter = {
  kind: "level" | "tag" | "source";
  id: string;
};

/**
 * The filter the exercise list is currently showing, or null when unfiltered.
 *
 * Prefer this over calling `isExercise*Active(location, null)`: those answer
 * "is the *null* filter active?", which is true precisely when the parameter is
 * ABSENT. Reading the id back out of such a branch yields null and builds
 * requests like `/tag/null/exercises`.
 */
export function getActiveExerciseFilter(
  location: RouteLocation
): ActiveExerciseFilter | null {
  const filter = getExerciseListFilter(location);
  if (!filter) {
    return null;
  }
  if (filter.levelId) {
    return { kind: "level", id: filter.levelId };
  }
  if (filter.tagId) {
    return { kind: "tag", id: filter.tagId };
  }
  if (filter.sourceId) {
    return { kind: "source", id: filter.sourceId };
  }
  return null;
}

export function buildExerciseLevelPath(levelId: string) {
  return routes.exercises.path(undefined, { query: { level_id: levelId } });
}

export function buildExerciseTagPath(tagId: string) {
  return routes.exercises.path(undefined, { query: { tag_id: tagId } });
}

export function buildExerciseSourcePath(sourceId: string) {
  return routes.exercises.path(undefined, { query: { source_id: sourceId } });
}

export function isExerciseLevelActive(location: RouteLocation, levelId: string | null) {
  return getExerciseListFilter(location)?.levelId === levelId;
}

export function isExerciseTagActive(location: RouteLocation, tagId: string | null) {
  return getExerciseListFilter(location)?.tagId === tagId;
}

export function isExerciseSourceActive(location: RouteLocation, sourceId: string | null) {
  return getExerciseListFilter(location)?.sourceId === sourceId;
}

export function isMyExerciseList(location: RouteLocation) {
  return routes.exercisesByCreator.match(location.pathname);
}
